import React, { useEffect, useState, useMemo } from "react";

export default function DonateModal({ isOpen, onClose }) {
    const presetAmounts = useMemo(() => [250,500, 1100, 5000], []);
    const [selectedAmount, setSelectedAmount] = useState(presetAmounts[0]);
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [want80G, setWant80G] = useState(false);
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState(null);

    useEffect(() => {
        if (!isOpen) {
            setSelectedAmount(presetAmounts[0]);
            setName("");
            setEmail("");
            setPhone("");
            setWant80G(false);
            setLoading(false);
            setStatus(null);
        }
    }, [isOpen, presetAmounts]);

    if (!isOpen) return null;

    const handlePreset = (amt) => setSelectedAmount(amt);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus(null);

        if (!name.trim() || !email.trim() || !phone.trim() || !selectedAmount) {
            setStatus({ type: "error", text: "Please fill all fields." });
            return;
        }

        setLoading(true);
        try {
            const res = await fetch("/api/donations", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    amount: Number(selectedAmount),
                    name: name.trim(),
                    email: email.trim(),
                    phone: phone.trim(),
                    want80G,
                }),
            });

            if (!res.ok) {
                const txt = await res.text();
                throw new Error(txt || "Save failed");
            }

            // don't assign to an unused variable — only parse JSON if you will use it
            // const responseBody = await res.json(); // <-- uncomment if you need the returned object

            setStatus({ type: "success", text: "Thank you — your donation has been recorded." });

            setTimeout(() => {
                setLoading(false);
                onClose();
            }, 900);
        } catch (err) {
            setStatus({ type: "error", text: err.message || "Submission failed" });
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center pt-28">
            <div className="absolute inset-0 bg-black/50" onClick={onClose} />

            <div className="relative w-full max-w-lg h-[620px] mx-4 bg-slate-800 rounded-lg border border-cyan-500/20 shadow-lg p-6">
           <h3 className="text-xl font-semibold text-white mb-4">Donate</h3>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-white">Choose Amount (INR)</label>
                        <div className="mt-3 flex gap-3 flex-wrap">
                            {presetAmounts.map((amt) => (
                                <button
                                    key={amt}
                                    type="button"
                                    onClick={() => handlePreset(amt)}
                                    className={`px-4 py-2 rounded-full text-white font-semibold ${selectedAmount === amt ? "bg-cyan-700" : "bg-cyan-600/80 hover:bg-cyan-700"}`}
                                >
                                    ₹{amt}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-white">Selected Amount (INR)</label>
                        <input
                            type="number"
                            value={selectedAmount}
                            onChange={(e) => setSelectedAmount(Number(e.target.value))}
                            className="mt-2 w-full rounded-lg border px-3 py-2"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-white">Name</label>
                        <input value={name} onChange={(e) => setName(e.target.value)} className="mt-2 w-full rounded-lg border px-3 py-2" placeholder="Full name" />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-white">Email</label>
                        <input value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2 w-full rounded-lg border px-3 py-2" placeholder="you@example.com" type="email" />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-white">Phone</label>
                        <input value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-2 w-full rounded-lg border px-3 py-2" placeholder="+91 9XXXXXXXXX" />
                    </div>

                    <div className="flex items-center gap-2">
                        <input id="80g" type="checkbox" checked={want80G} onChange={() => setWant80G((v) => !v)} />
                        <label htmlFor="80g" className="text-sm text-white">I want 80G certificate (send on email)</label>
                    </div>

                    {status && <div className={`text-sm ${status.type === "error" ? "text-rose-500" : "text-emerald-600"}`}>{status.text}</div>}

                    <div className="pt-3">
                        <button type="submit" disabled={loading} className="w-full bg-cyan-700 text-white py-2.5 rounded-full font-semibold hover:bg-cyan-800 disabled:opacity-60">
                            {loading ? "Processing..." : "Donate Now"}
                        </button>
                    </div>
                </form>

                <button onClick={onClose} className="absolute top-3 right-3 text-slate-500 hover:text-slate-700">✕</button>
            </div>
        </div>
    );
}